import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { isFileTypeValid } from '../../../../../backend/utils';
import { fileTypeToIconMap } from '../../../../features/file-manager';
import { useDocumentLibraryTranslations } from '../../../../hooks';
import { humanReadableFileSize } from '../../../../utils';
import { forbiddenFileNameCharactersRegex, maxFileSize } from '../../contants';
export const FileUploadRow = ({ extendedFiles, file, onSetFiles }) => {
    const { t } = useDocumentLibraryTranslations();
    const isImage = (file === null || file === void 0 ? void 0 : file.file.type) && file.file.type.startsWith('image/');
    const { icon: Icon, color } = fileTypeToIconMap[isImage ? 'image' : 'file'];
    const fileIsTooBig = file.file.size > maxFileSize;
    const fileSize = humanReadableFileSize(file.file.size);
    const settings = useSelector(getSettings);
    const supportedFileTypesSetting = settings.supportedFileTypes.supportedFileTypes;
    const handleChangeName = (value) => {
        const trimmedValue = value.trim();
        onSetFiles(extendedFiles.map((extendedFile) => {
            var _a;
            if (extendedFile.file.name === file.file.name) {
                const isDuplicateName = extendedFiles
                    .filter((duplicate) => duplicate.file.name !== file.file.name)
                    .some((duplicate) => duplicate.editedName.trim() === trimmedValue);
                const errors = {
                    duplicateNameError: !!trimmedValue && isDuplicateName,
                    noNameError: !trimmedValue,
                    forbiddenCharactersError: !!((_a = trimmedValue.match(forbiddenFileNameCharactersRegex)) === null || _a === void 0 ? void 0 : _a.length),
                    unsupportedFileTypeError: !isFileTypeValid(supportedFileTypesSetting, trimmedValue, file.file.type),
                };
                return Object.assign(Object.assign({}, extendedFile), { editedName: trimmedValue, errors });
            }
            return extendedFile;
        }));
    };
    const handleChangeSummary = (event) => {
        onSetFiles(extendedFiles.map((extendedFile) => {
            if (extendedFile.file.name === file.file.name) {
                return Object.assign(Object.assign({}, extendedFile), { summary: event.target.value });
            }
            return extendedFile;
        }));
    };
    const handleRemoveFile = () => {
        showConfirmDialog({
            title: (React.createElement(Box, null,
                React.createElement(Typography, { variant: 'h6' }, t('multipleFilesUpload.confirmRemoveFile.title')))),
            content: (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('multipleFilesUpload.confirmRemoveFile.content'))),
            confirmLabel: t('multipleFilesUpload.confirmRemoveFile.confirmLabel'),
            onConfirm: () => {
                onSetFiles(extendedFiles.filter((item) => item.file.name !== file.file.name));
                showAlertSnackbar({
                    message: t('multipleFilesUpload.fileDeleted'),
                    type: 'success',
                    autoHideDuration: 5000,
                });
            },
        });
    };
    const showError = () => {
        return Object.values(file.errors).some((error) => error);
    };
    const getHelperText = (file) => {
        if (file.errors.duplicateNameError) {
            return t('multipleFilesUpload.duplicateName');
        }
        if (file.errors.noNameError) {
            return t('multipleFilesUpload.noName');
        }
        if (file.errors.forbiddenCharactersError) {
            return t('multipleFilesUpload.forbiddenCharacterInFileName');
        }
        if (file.errors.unsupportedFileTypeError) {
            return t('multipleFilesUpload.unsupportedFileType');
        }
        return '';
    };
    return (React.createElement(Box, { sx: {
            backgroundColor: themeColors.background,
            p: 2,
            display: 'grid',
            gridTemplateColumns: fileIsTooBig ? '1fr 66% auto' : '1fr 33% 33% auto',
            gap: 2,
            alignItems: 'stretch',
        } },
        React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', wordWrap: 'normal', overflow: 'hidden' } },
            React.createElement(Icon, { sx: { mr: 2, color } }),
            React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', color: themeColors.text.main } },
                React.createElement(Typography, { variant: 'body2' }, file.file.name),
                React.createElement(Typography, { variant: 'caption' }, fileSize))),
        !fileIsTooBig && (React.createElement(React.Fragment, null,
            React.createElement(Box, null,
                React.createElement(TextField, { fullWidth: true, label: t('multipleFilesUpload.fileName'), defaultValue: file.editedName, onChange: (event) => handleChangeName(event.target.value), onPaste: (event) => {
                        if ('value' in event.target && typeof event.target.value === 'string') {
                            handleChangeName(event.target.value);
                        }
                    }, variant: 'outlined', error: showError(), helperText: getHelperText(file), required: true, inputProps: { maxLength: 200 } })),
            React.createElement(Box, null,
                React.createElement(TextField, { fullWidth: true, label: t('multipleFilesUpload.summary'), variant: 'outlined', onChange: handleChangeSummary, inputProps: { maxLength: 2000 } })))),
        fileIsTooBig && (React.createElement(Box, { sx: { display: 'flex', color: themeColors.accent.red.bold, gap: 1, alignItems: 'center' } },
            React.createElement(ErrorOutlineIcon, null),
            React.createElement(Typography, { variant: 'subtitle2' }, t('multipleFilesUpload.fileTooLargeError', {
                humanReadableFileSize: fileSize,
                maxFileSize: humanReadableFileSize(maxFileSize),
            })))),
        React.createElement(Box, null,
            React.createElement(IconButton, { color: 'primary', onClick: () => {
                    handleRemoveFile();
                }, "aria-label": t('multipleFilesUpload.deleteButtonAriaLabel') },
                React.createElement(DeleteOutlineIcon, null)))));
};
